'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { COOKIES, HttpStatus, NAVIGATION_URLS, type Nullable } from '@libs/core';
import type {
    AddDonationRequestBody,
    AddItemRequestBody,
    ApplyPromoCodeRequestBody,
    BaseError,
    CartBadRequestErrorResponse,
    CartCreateBody,
    CartUI,
    ErrorResponseBody,
    GuestEmailRequestBody,
    PickupInfoRequestBody,
    PlaceOrderRequestBody,
    RemoveItemRequestBody,
    ShippingMethods,
    TreatPointRequestBody,
    UpdateItemFulfillmentMethodRequestBody,
    UpdateItemQtyRequestBody,
} from '@libs/common-node';

import { getProductById, getProductUrl } from '@libs/algolia';
import Cookies from 'js-cookie';
import type { CartValidationErrorDetails, IdsList, ItemsListItem, StockErrorItem } from '../../types';
import { PageType } from '../../types';
import { getAutoshipPromo, getCartById, logConsoleError, updateSubscriptionStatus, validateCart } from '../../utils';
import fetchAndMergeStores from './fetchAndMergeStores';
import { asyncForEach } from './asyncForEach';
import { parseCheckoutPaymentErrorResponse, parseErrorResponse, unexpectedError } from './parseErrorResponse';
import { CartUpdateError, CartValidationError, CheckoutPaymentError, getShouldIgnoreError } from './error';
import { parseStockErrors } from './error/parseStockErrors';
import { CART_ERROR_MESSAGES, COMPONENTS } from '../../constants';
import { cartValidationNotificationMap, checkoutValidationNotificationMap } from '../../utils/notificationMaps';
import { useCartNotifications, usePageLayout } from '../../hooks';
import { getAccessToken } from '@libs/api-proxy-client/utils';
import { usePathname } from 'next/navigation';

type RequestBodyType =
    | { cartId: string }
    | CartCreateBody
    | AddItemRequestBody
    | RemoveItemRequestBody
    | UpdateItemQtyRequestBody
    | AddDonationRequestBody
    | GuestEmailRequestBody
    | PickupInfoRequestBody
    | ApplyPromoCodeRequestBody
    | PlaceOrderRequestBody
    | TreatPointRequestBody;

type CartUpdater<T = RequestBodyType> = (params: {
    itemId: string;
    cartId: string;
    version: number;
    body: T;
}) => Promise<Nullable<CartUI>>;

const FALLBACK_PRODUCT_URL = '#'; // Will be placed if cart has corrupted product, but TODO: it should be sorted out

/**
 * Determines if an HTTP response status code represents an error. Relies on the status being there.
 * Errors are considered in ranges:
 * - 4xx: Client errors
 * - 5xx: Server errors
 *
 * @param status - The HTTP status code to check.
 * @returns True if the status code represents an error; otherwise, false.
 */

const isErrorStatus = (status: number): boolean => {
    return status >= 400 && status < 600;
};

const pageTypesRequiringStoreInfo = [PageType.CART, PageType.CHECKOUT];

const useCartState = (cartId: string) => {
    const queryClient = useQueryClient();
    const { pageType, flags } = usePageLayout();
    const isLoggedIn = getAccessToken();
    const autoshipDiscountUpdatesEnabled = Boolean(flags?.autoshipDiscountUpdatesEnabled);

    const EMPTY_CART = null;

    const init = useRef(false);
    const pathname = usePathname();

    const [cart, setCart] = useState<Nullable<CartUI>>(EMPTY_CART);
    const [idsList, setIdsList] = useState<IdsList>([]);
    const [cartShippingOptionsMessage, setCartShippingOptionsMessage] = useState('');
    const [itemsList, setItemsList] = useState<ItemsListItem[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [isUpdating, setUpdating] = useState(false);
    const [errors, setErrors] = useState<CartValidationErrorDetails[]>([]);
    const [stockErrors, setStockErrors] = useState<StockErrorItem[]>([]);
    const { setNotifications } = useCartNotifications();
    const [isCartNotFound, setIsCartNotFound] = useState<boolean>();
    const [shippingMethodsList, setShippingMethodsList] = useState<ShippingMethods[]>([]);

    const createProductMetadata = useCallback(async (sku: string) => {
        const product = await getProductById(sku);

        return {
            productUrl: product ? getProductUrl(product) : FALLBACK_PRODUCT_URL,
            autoshipDiscount: product ? getAutoshipPromo(product.eligiblePromotions)?.discountDisplay : null,
            product,
        };
    }, []);

    const enrichCart = useCallback(
        async (cartRes: CartUI): Promise<CartUI> => {
            const ids: IdsList = [];
            const items: Array<ItemsListItem> = [];

            let enrichedCart = structuredClone(cartRes);

            await asyncForEach(enrichedCart.fulfillmentGroups, async group => {
                await asyncForEach(group.items, async item => {
                    ids.push(item.id);
                    const { productUrl, autoshipDiscount, product } = await createProductMetadata(item.sku);
                    item.productUrl = productUrl;
                    item.autoshipDiscount = autoshipDiscount;
                    item.productUrl = productUrl;
                    item.__position = product?.__position;
                    item.__queryID = product?.__queryID;
                    items.push({
                        ...item,
                        storeId: `${group.storeId}`,
                        fulfillmentMethod: group.fulfillmentMethod,
                    });
                });
            });

            if (pageTypesRequiringStoreInfo.includes(pageType)) {
                enrichedCart = await fetchAndMergeStores(enrichedCart, queryClient);
            }

            setIdsList(ids);
            setItemsList(items);

            return enrichedCart;
        },
        [createProductMetadata]
    );

    const handleCartError = useCallback(
        (error?: unknown): BaseError[] => {
            let parsedErrors: CartValidationErrorDetails[] = [];

            if (error instanceof CheckoutPaymentError) {
                parsedErrors = parseCheckoutPaymentErrorResponse(error.errorDetails);

                setErrors(parsedErrors);
            } else if (error instanceof CartUpdateError) {
                parsedErrors = parseErrorResponse(error.errorDetails, error?.attemptedFulfillmentMethod);
                setErrors(parsedErrors);

                if (parsedErrors.length) {
                    const parsedStockErrors = parseStockErrors(
                        parsedErrors,
                        itemsList,
                        error?.attemptedFulfillmentMethod
                    ) as StockErrorItem[];

                    setStockErrors(parsedStockErrors);
                }
            } else if (error instanceof CartValidationError) {
                parsedErrors = parseErrorResponse(error.errorDetails);
                setErrors(parsedErrors);

                if (parsedErrors.length) {
                    setStockErrors(parseStockErrors(parsedErrors, itemsList) as StockErrorItem[]);
                }
            } else if (error instanceof Error) {
                parsedErrors = [unexpectedError];
                setErrors(parsedErrors);
            }

            if (parsedErrors.length > 0) {
                for (const parsedError of parsedErrors) {
                    const errorCode = parsedError.errorCode;

                    const notification =
                        cartValidationNotificationMap[errorCode as keyof typeof cartValidationNotificationMap] ||
                        checkoutValidationNotificationMap[errorCode as keyof typeof checkoutValidationNotificationMap];

                    if (notification) {
                        logConsoleError(
                            `Error code: <${errorCode}> | Heading: <${notification.header}> | Message: <${
                                notification.text
                            }> - Error object: ${JSON.stringify(parsedError, null, 2)}`,
                            {
                                component: COMPONENTS.CART_PROVIDER,
                                severity: 'HIGH',
                                additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
                            }
                        );
                    } else {
                        logConsoleError(JSON.stringify(error), {
                            component: COMPONENTS.CART_PROVIDER,
                            severity: 'HIGH',
                            additionalInfo: `${CART_ERROR_MESSAGES.VALIDATE_CART_FAILED} | Error code: ${errorCode}`,
                        });
                    }
                }
            } else {
                logConsoleError(JSON.stringify(error), {
                    component: COMPONENTS.CART_PROVIDER,
                    severity: 'HIGH',
                    additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
                });
            }

            return parsedErrors;
        },
        [itemsList]
    );

    const removeErrorByCode = useCallback((errorCode: string) => {
        setErrors(currentErrors => currentErrors.filter(error => error.errorCode !== errorCode));
    }, []);

    const clearErrors = useCallback(() => setErrors([]), []);
    const clearStockErrors = useCallback(() => setStockErrors([]), []);

    const handleCartValidation = async (updatedCart: CartUI): Promise<void> => {
        try {
            // skip validation if cart is empty
            if (updatedCart.fulfillmentGroups?.length !== 0) {
                await validateCart({
                    cartId: updatedCart.id,
                    useRealtimeInventory: false,
                });
            }

            setErrors([]);
        } catch (error: unknown) {
            handleCartError(error);
            logConsoleError(JSON.stringify(error), {
                component: COMPONENTS.CART_PROVIDER,
                severity: 'HIGH',
                additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
            });
        }
    };

    const updateNotifications = (updatedCart: Nullable<CartUI>) => {
        if (updatedCart?.notifications) {
            setNotifications(updatedCart?.notifications ?? []);
        }
    };

    const updateCartState = async <T>(cartUpdater: CartUpdater<T>, body: Nullable<T> = null, itemId = '') => {
        if (!cart) {
            return;
        }
        try {
            setUpdating(true);

            let updatedCart = await cartUpdater({
                body: body as T,
                cartId: cart.id.toString(),
                itemId,
                version: cart.version,
            });

            const isCartUpdateFailure =
                updatedCart?.status &&
                isErrorStatus(updatedCart.status) &&
                !(updatedCart as unknown as ErrorResponseBody)?.invalid_fields;

            if (isCartUpdateFailure) {
                throw new CartUpdateError(
                    'Bad request',
                    updatedCart as unknown as CartBadRequestErrorResponse,
                    (body as UpdateItemFulfillmentMethodRequestBody)?.fulfillmentMethod
                );
            }

            if (updatedCart?.id) {
                updatedCart = await enrichCart(updatedCart);
                setCart(updatedCart);

                updateNotifications(updatedCart);

                await handleCartValidation(updatedCart);
            }

            return updatedCart;
        } catch (error: unknown) {
            logConsoleError(JSON.stringify(error), {
                component: COMPONENTS.CART_PROVIDER,
                severity: 'HIGH',
                additionalInfo: CART_ERROR_MESSAGES.CART_UPDATE_FAILED,
            });

            if (getShouldIgnoreError(error)) {
                return error as CartUI;
            }

            handleCartError(error);
        } finally {
            setUpdating(false);
        }
    };

    const clearCart = () => {
        setCart(EMPTY_CART);
        Cookies.remove(COOKIES.CART_ID);
        Cookies.remove(COOKIES.IS_SHIPPING_OPTION_CONFIRMED);
    };

    const refetchCart = useCallback(async () => {
        setUpdating(true);

        try {
            const cartRes = cartId ? await getCartById({ cartId }) : null;

            if (cartRes?.id) {
                const updatedCart = await enrichCart(cartRes);

                setCart(updatedCart);

                updateNotifications(updatedCart);

                return updatedCart;
            }
        } finally {
            setUpdating(false);
        }
    }, [cartId, enrichCart]);

    useEffect(() => {
        const isConfirmationPage = pathname === NAVIGATION_URLS.velocity.orderConfirmation;

        const initializeCart = async () => {
            if (init.current || isConfirmationPage) {
                return;
            }

            try {
                init.current = true;

                let cartRes = cartId ? await getCartById({ cartId }) : null;

                if (
                    cartRes?.id &&
                    cartRes?.version &&
                    pageType === PageType.CART &&
                    isLoggedIn &&
                    autoshipDiscountUpdatesEnabled
                ) {
                    const updatedCartWithSubscriptionStatus = await updateSubscriptionStatus({
                        cartId: cartRes.id,
                        version: cartRes.version,
                    });

                    if (updatedCartWithSubscriptionStatus?.id) {
                        cartRes = updatedCartWithSubscriptionStatus;
                    }
                }

                if (cartRes?.status === HttpStatus.NOT_FOUND) {
                    setIsCartNotFound(true);

                    logConsoleError(JSON.stringify(cartRes), {
                        component: COMPONENTS.CART_PROVIDER,
                        severity: 'HIGH',
                        additionalInfo: CART_ERROR_MESSAGES.CART_NOT_FOUND,
                    });
                    handleCartError(cartRes);

                    return;
                }

                if (cartRes?.id) {
                    const updatedCart = await enrichCart(cartRes);

                    setCart(updatedCart);

                    updateNotifications(updatedCart);
                }
            } catch (error) {
                handleCartError(error);
                logConsoleError(JSON.stringify(error), {
                    component: COMPONENTS.CART_PROVIDER,
                    severity: 'HIGH',
                    additionalInfo: CART_ERROR_MESSAGES.CART_INITIALIZATION_FAILED,
                });
            } finally {
                setLoading(false);
            }
        };

        initializeCart();
    }, [cartId, enrichCart, handleCartError]);

    return {
        cart,
        idsList,
        itemsList,
        isLoading,
        isUpdating,
        enrichCart,
        setCart,
        clearCart,
        setUpdating,
        updateCartState,
        errors,
        handleCartError,
        cartShippingOptionsMessage,
        setCartShippingOptionsMessage,
        removeErrorByCode,
        clearErrors,
        isCartNotFound,
        handleCartValidation,
        stockErrors,
        clearStockErrors,
        refetchCart,
        shippingMethodsList,
        setShippingMethodsList,
    };
};

export default useCartState;
